<template>
  <div class="inline-audio-player">
    <!-- Speaker / pause icon — absolutely positioned top-right of the parent bubble -->
    <button
      class="play-icon-btn"
      :class="{ playing: isPlaying, loading: isLoading }"
      :title="isPlaying ? 'Pause' : 'Read message aloud'"
      :disabled="hasError"
      @click="togglePlayPause"
    >
      <v-progress-circular
        v-if="isLoading"
        indeterminate
        size="14"
        width="2"
        color="primary"
      />
      <i
        v-else
        :class="isPlaying ? 'pi pi-pause' : 'pi pi-volume-up'"
        style="font-size: 18px;"
      />
    </button>

    <!-- Slim progress bar — shown while playing or after playback has started -->
    <div
      v-if="isPlaying || currentTime > 0"
      class="slim-progress"
    >
      <div
        class="slim-progress-fill"
        :style="{ width: progressPercent + '%' }"
      />
    </div>

    <!-- Subtle error indicator -->
    <span
      v-if="hasError"
      class="audio-error-hint"
      title="Audio unavailable"
    >
      <i
        class="pi pi-exclamation-circle"
        style="font-size: 14px; color: var(--red-500);"
      />
    </span>

    <!-- Hidden Audio Element -->
    <audio
      ref="audioElement"
      :src="audioUrl"
      preload="metadata"
      crossorigin="anonymous"
      @play="onPlayStart"
      @pause="onPlayEnd"
      @ended="onPlayEnd"
      @timeupdate="onTimeUpdate"
      @loadedmetadata="onLoadedMetadata"
      @canplay="onCanPlay"
      @error="onAudioError"
      @loadstart="onLoadStart"
    />
  </div>
</template>

<script>
import audioService from '@/services/audioService'
import { AUDIO_TTS_FAILED } from '@/assets/constants/constants'

export default {
  name: 'AudioPlayer',

  props: {
    /**
     * Presigned S3 URL for the audio file
     */
    audioUrl: {
      type: String,
      required: true,
      validator: (url) => audioService.isValidPresignedUrl(url)
    },

    /**
     * Duration of audio in milliseconds (from server)
     */
    durationMs: {
      type: Number,
      default: 0
    },

    /**
     * Optional message ID for tracking
     */
    messageId: {
      type: String,
      default: null
    },

    /**
     * Auto-play when loaded
     */
    autoPlay: {
      type: Boolean,
      default: false
    }
  },

  emits: ['play-started', 'play-ended', 'error', 'loaded'],

  data() {
    return {
      isPlaying: false,
      isLoading: true,
      isReady: false,
      currentTime: 0,
      duration: 0,
      hasError: false,
      errorMessage: AUDIO_TTS_FAILED,
      retryCount: 0,
      maxRetries: 2
    }
  },

  computed: {
    progressPercent() {
      if (!this.duration) return 0
      return Math.min((this.currentTime / this.duration) * 100, 100)
    }
  },

  watch: {
    audioUrl() {
      this.resetPlayer()
    }
  },

  beforeUnmount() {
    this.cleanup()
  },

  methods: {
    togglePlayPause() {
      if (!this.$refs.audioElement) return

      if (this.isPlaying) {
        this.$refs.audioElement.pause()
      } else {
        this.$refs.audioElement.play().catch((error) => {
          this.hasError = true
          this.errorMessage = 'Failed to play audio'
          this.$emit('error', error)
        })
      }
    },

    onPlayStart() {
      this.isPlaying = true
      this.$emit('play-started', { messageId: this.messageId })
    },

    onPlayEnd() {
      this.isPlaying = false
      this.$emit('play-ended', { messageId: this.messageId })
    },

    onTimeUpdate() {
      if (this.$refs.audioElement) {
        this.currentTime = this.$refs.audioElement.currentTime
      }
    },

    onLoadedMetadata() {
      if (this.$refs.audioElement) {
        // Use metadata duration if available, fallback to prop
        const audioDuration = this.$refs.audioElement.duration
        this.duration = isFinite(audioDuration) ? audioDuration : this.durationMs / 1000
      }
    },

    onCanPlay() {
      this.isReady = true
      this.isLoading = false
      this.hasError = false
      this.retryCount = 0

      if (this.autoPlay && this.$refs.audioElement) {
        this.$refs.audioElement.play().catch(() => {
          // Autoplay might fail due to browser policies
        })
      }

      this.$emit('loaded', { messageId: this.messageId, duration: this.duration })
    },

    onLoadStart() {
      this.isLoading = true
      this.isReady = false
    },

    onAudioError() {
      if (!this.$refs.audioElement) return

      const error = this.$refs.audioElement.error

      let message = 'Failed to load audio'
      if (error) {
        switch (error.code) {
          case error.MEDIA_ERR_NETWORK:
            message = 'Network error while loading audio'
            break
          case error.MEDIA_ERR_DECODE:
            message = 'Unable to decode audio format'
            break
          case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
            message = 'Audio format not supported'
            break
          case error.MEDIA_ERR_ABORTED:
            message = 'Audio loading aborted'
            break
        }
      }

      this.errorMessage = message
      this.isLoading = false
      this.isReady = false

      // Attempt retry
      if (this.retryCount < this.maxRetries) {
        this.retryCount++
        setTimeout(() => {
          this.retryLoad()
        }, 1000 * this.retryCount) // Exponential backoff
      } else {
        this.hasError = true
        this.$emit('error', { messageId: this.messageId, message })
      }
    },

    retryLoad() {
      if (!this.$refs.audioElement) return

      // Reset error state and reload
      this.hasError = false
      this.isLoading = true
      this.$refs.audioElement.load()
    },    resetPlayer() {
      this.isPlaying = false
      this.isLoading = true
      this.isReady = false
      this.currentTime = 0
      this.duration = 0
      this.hasError = false
      this.retryCount = 0

      if (this.$refs.audioElement) {
        this.$refs.audioElement.pause()
        this.$refs.audioElement.currentTime = 0
      }
    },

    cleanup() {
      if (this.$refs.audioElement) {
        this.$refs.audioElement.pause()
        this.$refs.audioElement.src = ''
      }
    }
  }
}
</script>

<style scoped>
/* The component renders inside the AI message bubble (position: relative).
   The play button is absolutely positioned top-right. */

.play-icon-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: #6b7280;
  transition: background 0.15s ease, color 0.15s ease;
  padding: 0;
}

.play-icon-btn:hover:not(:disabled) {
  background: rgba(30, 37, 87, 0.08);
  color: #1e2557;
}

.play-icon-btn.playing {
  color: #1e2557;
}

.play-icon-btn:disabled {
  opacity: 0.4;
  cursor: default;
}

.slim-progress {
  margin-top: 8px;
  height: 3px;
  background: rgba(30, 37, 87, 0.12);
  border-radius: 2px;
  overflow: hidden;
}

.slim-progress-fill {
  height: 100%;
  background: #1e2557;
  border-radius: 2px;
  transition: width 0.1s linear;
}

.audio-error-hint {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
}
</style>
